import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { toast } from "react-toastify";
import api from "../../api";

const VERDICTS = {
  approved: { label: "Approved", color: "success", icon: <CheckCircleOutlineIcon fontSize="small" /> },
  rejected: { label: "Rejected", color: "error", icon: <CancelOutlinedIcon fontSize="small" /> },
};

/**
 * A tester watches one finished explanation video and approves or rejects it. Only approved
 * videos can be published. When a new version waits, that is the one being verified.
 *
 * question: a row of /sets/{type}/{id}/questions; type: worksheet | mock | paper.
 */
export default function VideoVerifyDialog({ open, type, question, onClose, onSaved }) {
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStatus(question?.verification || "");
    setNotes(question?.verification_notes || "");
  }, [open, question]);

  if (!question) return null;
  const newVersion = Boolean(question.pending_video_url);
  const url = question.pending_video_url || question.video_url;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/admin/explanation-videos/questions/${type}/${question.id}/verify`, {
        status,
        notes: notes.trim() || null,
      });
      toast.success(res.data?.message || "Saved.");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save the verdict.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Verify video · Q{question.number}
        <Typography variant="body2" color="text.secondary">
          {question.text}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {newVersion && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            This is the new version. Students keep the current video until the new one is verified and published.
          </Alert>
        )}
        {question.state === "flagged" && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            The automatic check flagged this video{question.qa_notes ? `: ${question.qa_notes}` : "."} Approving clears the flag.
          </Alert>
        )}
        <Box sx={{ bgcolor: "#0b0b12", borderRadius: 1, overflow: "hidden", aspectRatio: "16 / 9" }}>
          {url && <video key={url} src={url} controls style={{ width: "100%", height: "100%", display: "block" }} />}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mt: 2 }}>
          Your verdict
        </Typography>
        <RadioGroup row value={status} onChange={(e) => setStatus(e.target.value)}>
          {Object.entries(VERDICTS).map(([value, v]) => (
            <FormControlLabel key={value} value={value} control={<Radio size="small" color={v.color} />} label={v.label} />
          ))}
        </RadioGroup>
        {status === "rejected" && question.published && !newVersion && (
          <Typography variant="caption" color="warning.main" display="block">
            This video is published. Rejecting it hides it from students and parents straight away.
          </Typography>
        )}
        <TextField
          label={status === "rejected" ? "What is wrong (required)" : "Notes (optional)"}
          multiline
          minRows={2}
          fullWidth
          size="small"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          error={status === "rejected" && !notes.trim()}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={VERDICTS[status]?.color ?? "primary"}
          disabled={submitting || !status || (status === "rejected" && !notes.trim())}
          startIcon={submitting ? <CircularProgress size={16} /> : VERDICTS[status]?.icon}
          onClick={submit}
        >
          {submitting ? "Saving…" : status ? `Submit (${VERDICTS[status].label})` : "Select a verdict"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
